import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { Plus, AlertTriangle, ChevronDown, Trash2 } from '../../icons';
import type { TeamDto } from '../../api/project-teams';
import type { MyTaskDto, TaskProjectDto } from '../../api/tasks';
import { FavoriteStar } from '../../components/Favorites';
import { ProgressRing, accentOf, isOverdue, MemberStack } from './ProjectHomeSpaces';
import type { ProjectHomeSummaryDto } from '../../api/project-home';
import { liveProjects } from './space-cards';
import { confirmAndDeleteProject, isProjectCreator } from './confirmDeleteProject';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './ProjectHomeSpaces.css';

/**
 * ProjectHomeProjects (2026-08-21) — 프로젝트 홈의 **'내 프로젝트' 카드 격자**.
 *
 * 종전에 좌측 네비의 [내 프로젝트] 는 `/task` 로 곧장 갔다. 그런데 그 화면은 목록이 아니라
 * **프로젝트 하나의 칸반 보드**라, 누른 사람 입장에서는 "내 프로젝트 목록" 을 눌렀는데
 * 난데없이 어떤 프로젝트 안에 들어가 있게 됐다(2026-08-21 신고).
 * 이제 [내 프로젝트] 는 이 격자를 컨텐츠 영역에 띄우고, 카드를 눌러야 그 보드로 들어간다.
 *
 * 생김새는 '내 스페이스' 카드와 **같은 클래스**(.pj-space-grid / .pj-space-card …)를 쓴다.
 * 한 화면에 나란히 서는 두 목록이라 따로 만들면 곧 어긋난다 — 링·팔레트·지연 판정까지
 * ProjectHomeSpaces 에서 가져다 쓴다.
 *
 * 데이터는 전부 부모가 이미 들고 있는 것(projects · teams · myTasks)으로 만든다 — 서버 변경 0.
 */

/** 스페이스 필터 값 — '' = 전체, 'personal' = 무소속(개인), 그 외 = teamId. */
type SpaceFilter = string;

export interface ProjectHomeProjectsProps {
  projects: TaskProjectDto[];
  teams: TeamDto[];
  myTasks: MyTaskDto[];
  onOpenProject: (projectId: string) => void;
  onCreateProject: () => void;
  /**
   * AW-005 (2026-08-25) — 지연 건수의 출처. 스페이스 카드와 **같은 서버 집계**를 쓴다.
   * 종전에는 `/tasks/me` 로 받아온 범위에서만 세어, 같은 화면의 다른 숫자와 어긋났다.
   * 없으면(구버전 서버·조회 실패) 종전 파생 경로로 떨어진다.
   */
  summary?: ProjectHomeSummaryDto | null;
  /**
   * 2026-09-01 — 카드의 [🗑] 삭제가 끝났을 때 부모가 목록을 다시 읽는 경로.
   * 스페이스 카드의 `onSpaceDeleted` 와 같은 계약이다 — **주지 않으면 삭제 버튼도 뜨지 않는다**.
   * (지운 뒤 격자가 그대로면 방금 지운 카드가 남아 있는 것처럼 보이기 때문.)
   */
  onProjectDeleted?: (projectId: string) => void;
}

export function ProjectHomeProjects({
  projects, teams, myTasks, onOpenProject, onCreateProject, summary = null,
  onProjectDeleted,
}: ProjectHomeProjectsProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const [filter, setFilter] = useState<SpaceFilter>('');

  // 보관된 프로젝트는 카드로 세우지 않는다 — 스페이스 카드가 쓰는 기준과 같은 함수.
  const live = useMemo(() => liveProjects(projects), [projects]);

  const shown = useMemo(() => {
    if (filter === '') return live;
    if (filter === 'personal') return live.filter((p) => !p.teamId);
    return live.filter((p) => p.teamId === filter);
  }, [live, filter]);

  /**
   * 프로젝트별 지연 건수 — 스페이스 카드와 같은 규칙.
   * 서버 집계가 있으면 그것을, 없으면 종전 파생(미완료 + 마감 경과)을 쓴다.
   * "정말 0" 과 "구버전 서버라 필드 없음" 을 가르는 판정도 스페이스 카드와 동일하다.
   */
  const overdueOf = useMemo(() => {
    // 정규화를 거치지 않은 summary 객체도 들어올 수 있어 필드 부재를 빈 배열로 받는다.
    const rows = summary?.overdueByProject ?? [];
    if (summary && !(rows.length === 0 && summary.myTasks.overdue > 0)) {
      const sm = new Map<string, number>();
      for (const r of rows) sm.set(r.projectId, r.count);
      return sm;
    }
    const m = new Map<string, number>();
    for (const t of myTasks) {
      if (!isOverdue(t)) continue;
      m.set(t.project.id, (m.get(t.project.id) ?? 0) + 1);
    }
    return m;
  }, [myTasks, summary]);

  /**
   * 2026-09-01 — 카드의 [🗑] 삭제. 스페이스 카드(ProjectHomeSpaces)와 같은 흐름이다:
   * 확인창에서 함께 사라질 할일 수·제목을 먼저 보여 주고(confirmAndDeleteProject), 지워지면
   * 부모가 목록을 다시 읽는다. 진행 중에는 다른 카드의 삭제를 받지 않는다 — 확인창이 둘
   * 겹치면 어느 프로젝트를 지우는지 알 수 없다.
   *
   * 권한은 서버가 SSOT(생성자만) — 버튼 노출은 그 UI 미러(isProjectCreator)다.
   */
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const handleDeleteProject = useCallback(async (p: TaskProjectDto) => {
    if (deletingId !== null) return;
    setDeletingId(p.id);
    try {
      if (await confirmAndDeleteProject(p)) onProjectDeleted?.(p.id);
    } finally {
      setDeletingId(null);
    }
  }, [deletingId, onProjectDeleted]);

  return (
    <div className="pj-spaces">
      <div className="pj-space-sectionhead">
        <h2 className="pj-space-sectiontitle">{tProject('project.home.my_projects')}</h2>
        <div className="pj-team-select-wrap pj-team-select-wrap--inline">
          <select
            className="pj-team-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label={tProject('project.detail.projects.team_filter_aria')}
          >
            <option value="">{tProject('project.detail.projects.filter_all')}</option>
            <option value="personal">{tProject('project.team.personal')}</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <ChevronDown size={14} className="pj-team-select-chevron" />
        </div>
      </div>

      <div className="pj-space-grid">
        {shown.map((p) => {
          const total = p.taskCount ?? 0;
          const done = p.doneTaskCount ?? 0;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          const overdue = overdueOf.get(p.id) ?? 0;
          // 삭제 진입점은 생성자에게만, 그리고 부모가 갱신 경로를 준 경우에만.
          const deletable = onProjectDeleted != null && isProjectCreator(p);
          return (
            /* 카드가 통째로 버튼이라 별을 안에 넣을 수 없다 — 스페이스 카드와 같은 래퍼 방식. */
            <div
              className={`pj-space-cardwrap${deletable ? ' pj-space-cardwrap--deletable' : ''}`}
              key={p.id}
            >
              <button
                type="button"
                className="pj-space-card"
                style={{ '--pj-space-accent': p.color ?? accentOf(p.id) } as React.CSSProperties}
                onClick={() => onOpenProject(p.id)}
                aria-label={tProject('project.home.aw001.project_open_aria', { name: p.name })}
              >
                <span className="pj-space-card-top">
                  <ProgressRing pct={pct} />
                  {overdue > 0 && (
                    <span className="pj-space-chip pj-space-chip--warn">
                      <AlertTriangle size={12} aria-hidden />
                      {tProject('project.home.aw001.space_overdue', { n: overdue })}
                    </span>
                  )}
                </span>
                <span className="pj-space-card-name">{p.name}</span>
                <span className="pj-space-card-meta">
                  {tProject('project.home.aw001.project_meta', {
                    space: p.teamName ?? tProject('project.team.personal'),
                    done: String(done),
                    total: String(total),
                  })}
                </span>
                {/* AW-005 — 스페이스 카드와 같은 아바타 스택. memberPreview 는 이미 목록 응답에 있다. */}
                <MemberStack members={p.memberPreview ?? []} total={p.memberCount} />
              </button>
              {/* 즐겨찾기 — 카드를 눌렀을 때 가는 곳과 같은 경로를 담는다. */}
              <FavoriteStar
                to={p.teamId ? `/task?team=${p.teamId}&project=${p.id}` : `/task?project=${p.id}`}
                label={p.name}
                className="pj-space-fav"
              />
              {/*
                삭제도 별과 같은 방식으로 카드 버튼의 **형제**로 얹는다(버튼 중첩은 무효 마크업).
                CSS 는 스페이스 카드가 쓰는 .pj-space-del 을 그대로 쓴다 — 두 격자가 한 화면에
                나란히 서므로 생김새가 갈라지면 안 된다.
              */}
              {deletable && (
                <button
                  type="button"
                  className="pj-space-del"
                  onClick={() => { void handleDeleteProject(p); }}
                  disabled={deletingId !== null}
                  aria-label={tProject('project.home.aw001.project_delete_aria', { name: p.name })}
                  data-tip={tProject('project.home.aw001.project_delete_aria', { name: p.name })}
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              )}
            </div>
          );
        })}

        <button type="button" className="pj-space-card pj-space-card--new" onClick={onCreateProject}>
          <Plus size={24} aria-hidden />
          <span>{tProject('project.btn.new_project')}</span>
        </button>

        {shown.length === 0 && (
          <p className="pj-space-empty" role="status">
            {tProject('project.home.aw001.projects_empty')}
          </p>
        )}
      </div>
    </div>
  );
}
